import { useCallback, useEffect, useRef } from 'react';
import { sessionExpired } from './api';

/**
 * usePollWhenVisible(load, opts) — call load() on a timer, but only while the tab is visible.
 *
 *   · nothing runs while document.hidden; when the tab comes back: one immediate load, then the timer
 *   · `fast` cadence while the user has done something here in the last `activeFor` ms, else `slow`
 *   · returns bump(): call it from anything deliberate (Refresh button, a send, opening a thread) to
 *     say "someone is active here" — an idle page goes straight back to the fast cadence
 *   · stops for good once sessionExpired() (see utils/api) — never polls a dead session
 *
 * Replaces the always-on 30 s setInterval the job-message pages had: same cadence while someone is
 * acting, 10× fewer requests while they sit idle, zero while the tab is hidden.
 */
export default function usePollWhenVisible(load, { enabled = true, fast = 30000, slow = 300000, activeFor = 120000 } = {}) {
  const loadRef = useRef(load); loadRef.current = load;      // always the latest closure, no re-subscribing
  const lastActive = useRef(Date.now());                      // mount counts as activity
  const timer = useRef(null);
  const scheduleRef = useRef(() => {});

  useEffect(() => {
    if (!enabled) return undefined;
    lastActive.current = Date.now();                          // (re)starting the poll counts as activity
    let stopped = false;
    const clear = () => { clearTimeout(timer.current); timer.current = null; };
    const schedule = () => {
      clear();
      if (stopped || document.hidden) return;                 // hidden: wait for visibilitychange instead
      const delay = Date.now() - lastActive.current < activeFor ? fast : slow;
      timer.current = setTimeout(() => {
        timer.current = null;
        if (stopped || sessionExpired()) return;              // dead session: stop, nothing reschedules
        if (!document.hidden) loadRef.current();
        schedule();
      }, delay);
    };
    scheduleRef.current = schedule;
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || stopped || sessionExpired()) return;
      lastActive.current = Date.now();
      loadRef.current();
      schedule();
    };
    document.addEventListener('visibilitychange', onVisible);
    schedule();
    return () => { stopped = true; clear(); scheduleRef.current = () => {}; document.removeEventListener('visibilitychange', onVisible); };
  }, [enabled, fast, slow, activeFor]);

  return useCallback(() => {
    const wasIdle = Date.now() - lastActive.current >= activeFor;
    lastActive.current = Date.now();
    if (wasIdle) scheduleRef.current();                       // drop the pending slow timer for a fast one
  }, [activeFor]);
}
